/**
 * The shared map state (docs/specs/map.md §6; inventory 05 F15, F16; MB Q69 decision 96).
 *
 * Every map window of Teslyn Field (the Map screen, any number of pop-out map windows, the tracker-only window)
 * shows one state held by the Rust core. A window changes it only through `applyMapAction` on the latest version it
 * has, and sends the result with that version (`map_state_update { base_version }`); the core refuses a stale
 * version, so the window re-applies its action to the newer state and sends again. Nothing is last-writer-wins
 * (the Diurnal pop-out lost pins and layers that way, inventory 05 finding 3).
 *
 * Two parts are kept: `prefs` are the user's view of the project (layers, base map, opacities, pinned items),
 * stored per user per project in the app data folder; `layers` are project data (the hazard and KML files shown,
 * the landholder parcels), stored in the project's `Teslyn` folder. What each window shows itself (its centre,
 * zoom and the aircraft it follows) is not shared: each window may follow a different aircraft (Q69).
 */
import { MAP_LAYERS, type MapLayerId } from './map-layers.js';
import type { LandParcel } from './map-kml.js';
import { BASE_LAYERS, OVERLAYS, type BaseLayerId, type OverlayId } from './tile-cache.js';
import type { GeoBounds } from './map-geo.js';
import type { MapImageOverlay } from './map-drop.js';
import { alignedBounds, normaliseImageAlignment, type ImageAlignment } from './image-align.js';
import {
  DEFAULT_WEATHER_PREFS,
  normaliseWeatherPrefs,
  setWeatherWidget,
  toggleWeatherMode,
  RADAR_COVERAGE_MAX,
  RADAR_COVERAGE_MIN,
  type WeatherCanvasId,
  type WeatherModeId,
  type WeatherPrefs,
  type WeatherWidgetId,
} from './weather.js';
import { HISTORY_DEPTH_HOURS } from './tracker.js';
import { DEFAULT_ELEVATION_CHOICE, parseElevationChoice, type ElevationChoice } from './elevation.js';
import { NOTAM_CATEGORIES, type NotamCategory } from './notam-map.js';
import { isNaipsAreaCode, type NaipsAreaMode } from './naips-areas.js';
import { NAIPS_MAX_AREAS } from './provider-protocols.js';
import { DEFAULT_PUBLIC_TRAFFIC, normalisePublicTraffic, type PublicTrafficPrefs } from './public-traffic.js';
import { DEFAULT_TRAIL_STYLE, normaliseTrailStyle, type TrailStyle } from './trail-details.js';
import type { PublicTrafficId } from './tracker.js';
import { AIRSPACE_GROUPS, DEFAULT_HIDDEN_AIRSPACE, type AirspaceGroup } from './openaip.js';
import { PROPERTY_LAYER_IDS, type PropertyLayerId } from './property-layers.js';

/** An item pinned to the map's "Pinned" list: zoom to it and keep it highlighted. */
export interface PinnedItem {
  /** `<layer>:<feature id>`. */
  readonly key: string;
  readonly label: string;
  readonly bounds: GeoBounds;
}

export interface MapPrefs {
  readonly base: BaseLayerId;
  readonly overlays: readonly OverlayId[];
  readonly layers: Readonly<Record<MapLayerId, boolean>>;
  /** Opacity of each project layer, 0.1 to 1. */
  readonly opacity: Readonly<Partial<Record<MapLayerId, number>>>;
  /** Hazard folder paths hidden in the layer tree (e.g. "Powerlines / Poles"). */
  readonly hidden_hazard_folders: readonly string[];
  /** The lines-remaining map's "Logged coverage" toggle. */
  readonly coverage: boolean;
  readonly pinned: readonly PinnedItem[];
  /** The weather mode bar, panels and opacities (docs/specs/map.md §15). */
  readonly weather: WeatherPrefs;
  /** The tracker's fleet groups ticked (03-004): none means every group; kept across restarts (bug 03-6). */
  readonly fleet_groups: readonly string[];
  /** The tracker's history depth in hours (03-028): one of 1, 2, 4, 6, 12, 24, 48; 12 by default. */
  readonly trail_hours: number;
  /** The NOTAM layer's switches and areas (docs/specs/map.md §14). */
  readonly notam: NotamPrefs;
  /** Public air traffic switched on, with each source's radius (03-013 to 03-016; `public-traffic.ts`). */
  readonly traffic: PublicTrafficPrefs;
  /** Trail opacity and weight (03-029). */
  readonly trail_style: TrailStyle;
  /** The airspace groups switched off (docs/specs/map.md §19.3). */
  readonly hidden_airspace: readonly AirspaceGroup[];
  /** KML and KMZ files hidden in the Files list (05-015), kept across restarts (bug 05-11). */
  readonly hidden_kml: readonly string[];
  /** KML files sent to the 3D view with "View in 3D" (05-015) or loaded there (05-048), the newest last. */
  readonly kml_3d: readonly string[];
  /** The 3D view and the map windows follow each other's camera (05-050; on by default, as the Diurnal tool). */
  readonly camera_sync: boolean;
  /** Auto-pin Oz Runways and AvPlan aircraft over the survey (03-042): on by default, as the Diurnal tool. */
  readonly auto_pin: boolean;
  /** Pinned aircraft (callsigns) whose trail is hidden (03-041, the eye button; the tool forgot it on restart). */
  readonly hidden_trails: readonly string[];
  /** The state property boundary layers switched on (04-021, `property-layers.ts`), none by default as the tool. */
  readonly property_layers: readonly PropertyLayerId[];
  /** Each property layer's opacity, 0 to 1, when changed from the tool's (`propertyOpacity`). */
  readonly property_opacity: Readonly<Partial<Record<PropertyLayerId, number>>>;
  /** Landholder boundaries hidden on this laptop (05-017; the record is shared, showing it is each laptop's own). */
  readonly hidden_landholders: readonly string[];
  /** The landholder boundaries' fill opacity, 0 to 1 (05-017; 15 % as the tool). */
  readonly landholder_fill: number;
  /** Landholder folders made on this laptop and not used yet (a folder in use is the parcels' own). */
  readonly landholder_folders: readonly string[];
  /** The fire layer also shows planned burns without a warning (docs/specs/map.md §29); off by default. */
  readonly fire_planned_burns: boolean;
}

/**
 * What a map starts with before anyone has changed it (05-005, decision 411): the organisation's first-launch
 * layers. Teslyn's own default is none (Satellite, no overlays); Thomson's is the Diurnal tool's set
 * (`content/tenants/thomson-airborne/map-defaults.json`).
 */
export interface FirstLaunchLayers {
  readonly overlays: readonly OverlayId[];
  /** Layers switched on beyond the defaults. */
  readonly layers: readonly MapLayerId[];
  /** Weather panels open. */
  readonly widgets: readonly WeatherWidgetId[];
}

/** A first-launch set made safe: unknown overlays, layers and panels dropped. */
export function normaliseFirstLaunch(raw: unknown): FirstLaunchLayers | null {
  if (!isObject(raw)) return null;
  const overlayIds = new Set<string>(OVERLAYS.map((o) => o.id));
  const layerIds = new Set<string>(MAP_LAYERS.map((l) => l.id));
  const widgetIds = new Set<string>(['wind', 'sky', 'rain', 'radar']);
  return {
    overlays: [...new Set(strings(raw.overlays).filter((o) => overlayIds.has(o)))] as OverlayId[],
    layers: [...new Set(strings(raw.layers).filter((l) => layerIds.has(l)))] as MapLayerId[],
    widgets: [...new Set(strings(raw.widgets).filter((w) => widgetIds.has(w)))] as WeatherWidgetId[],
  };
}

/** The preferences of a map nobody has changed yet, with the organisation's first-launch layers. */
export function firstLaunchPrefs(first: FirstLaunchLayers | null): MapPrefs {
  if (!first) return DEFAULT_MAP_PREFS;
  let weather = DEFAULT_MAP_PREFS.weather;
  for (const w of first.widgets) weather = setWeatherWidget(weather, w, true);
  return {
    ...DEFAULT_MAP_PREFS,
    overlays: [...first.overlays],
    layers: {
      ...DEFAULT_MAP_PREFS.layers,
      ...Object.fromEntries(first.layers.map((l) => [l, true])),
    } as Record<MapLayerId, boolean>,
    weather,
  };
}

/** The NOTAM layer's choices for a project (§14.2, §14.6), kept with the other map preferences. */
export interface NotamPrefs {
  /** Categories switched off. */
  readonly hidden: readonly NotamCategory[];
  /** "Hide NOTAMs above 10,000 ft AMSL" (on by default, as the Diurnal tool). */
  readonly ceiling: boolean;
  /** Around the project, all of Australia, or the areas typed (04-006). */
  readonly area_mode: NaipsAreaMode;
  /** The areas typed for "Chosen areas", up to five. */
  readonly areas: readonly string[];
}

export const DEFAULT_NOTAM_PREFS: NotamPrefs = { hidden: [], ceiling: true, area_mode: 'auto', areas: [] };

export interface MapProjectLayers {
  /** Hazard maps shown (`*_hazard.kml` paths), in the order loaded. */
  readonly hazard_files: readonly string[];
  /** KML or KMZ overlays shown. */
  readonly kml_files: readonly string[];
  /** Survey line files shown (`.lns`, or a survey-lines JSON), item 04-054. */
  readonly lns_files: readonly string[];
  /** Images shown on the map (dropped onto it), item 04-054. */
  readonly images: readonly MapImageOverlay[];
  readonly landholders: readonly LandParcel[];
  /** The 3D view's elevation for the project (Project settings; decisions 648, 707): Copernicus or bare earth. */
  readonly elevation: ElevationChoice;
}

export interface MapState {
  /** Increases by one on every change the core accepts. */
  readonly version: number;
  readonly prefs: MapPrefs;
  readonly layers: MapProjectLayers;
}

export const DEFAULT_MAP_PREFS: MapPrefs = {
  base: 'satellite',
  overlays: [],
  layers: Object.fromEntries(MAP_LAYERS.map((l) => [l.id, l.defaultOn])) as Record<MapLayerId, boolean>,
  opacity: {},
  hidden_hazard_folders: [],
  coverage: false,
  pinned: [],
  weather: DEFAULT_WEATHER_PREFS,
  fleet_groups: [],
  trail_hours: 12,
  notam: DEFAULT_NOTAM_PREFS,
  traffic: DEFAULT_PUBLIC_TRAFFIC,
  trail_style: DEFAULT_TRAIL_STYLE,
  hidden_airspace: DEFAULT_HIDDEN_AIRSPACE,
  hidden_kml: [],
  kml_3d: [],
  camera_sync: true,
  auto_pin: true,
  hidden_trails: [],
  property_layers: [],
  property_opacity: {},
  hidden_landholders: [],
  landholder_fill: 0.15,
  landholder_folders: [],
  fire_planned_burns: false,
};

/** A history depth choice (1 to 48 hours, decision 75), else null. */
const trailHours = (v: unknown): number | null =>
  typeof v === 'number' && (HISTORY_DEPTH_HOURS as readonly number[]).includes(v) ? v : null;

const groupList = (v: readonly string[]): string[] =>
  [...new Set(v.map((g) => g.trim()).filter((g) => g !== ''))].sort((a, b) => a.localeCompare(b));

export const EMPTY_PROJECT_LAYERS: MapProjectLayers = {
  hazard_files: [],
  kml_files: [],
  lns_files: [],
  images: [],
  landholders: [],
  elevation: DEFAULT_ELEVATION_CHOICE,
};

export const initialMapState = (): MapState => ({
  version: 0,
  prefs: DEFAULT_MAP_PREFS,
  layers: EMPTY_PROJECT_LAYERS,
});

export const OPACITY_MIN = 0.1;

const clampOpacity = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(1, Math.max(OPACITY_MIN, v)) : null;

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const strings = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];

const CATEGORY_IDS = new Set<string>(NOTAM_CATEGORIES.map((c) => c.id));
const PROPERTY_IDS = new Set<string>(PROPERTY_LAYER_IDS);

/** A property layer's opacity, 0 to 1 (the tool's slider went to 0), else null. */
const unitOpacity = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : null;

function propertyOpacities(v: unknown): Partial<Record<PropertyLayerId, number>> {
  const out: Partial<Record<PropertyLayerId, number>> = {};
  if (!isObject(v)) return out;
  for (const [k, x] of Object.entries(v)) {
    const o = unitOpacity(x);
    if (PROPERTY_IDS.has(k) && o !== null) out[k as PropertyLayerId] = o;
  }
  return out;
}
const AIRSPACE_GROUP_IDS = new Set<string>(AIRSPACE_GROUPS.map((g) => g.id));
const AREA_MODES = new Set<string>(['auto', 'all', 'manual']);

/** Stored NOTAM choices made safe: unknown categories and modes dropped, at most five valid area codes. */
export function normaliseNotamPrefs(v: unknown): NotamPrefs {
  if (!isObject(v)) return DEFAULT_NOTAM_PREFS;
  return {
    hidden: [...new Set(strings(v.hidden).filter((c) => CATEGORY_IDS.has(c)))] as NotamCategory[],
    ceiling: v.ceiling !== false,
    area_mode:
      typeof v.area_mode === 'string' && AREA_MODES.has(v.area_mode)
        ? (v.area_mode as NaipsAreaMode)
        : DEFAULT_NOTAM_PREFS.area_mode,
    areas: [
      ...new Set(
        strings(v.areas)
          .map((a) => a.trim())
          .filter(isNaipsAreaCode),
      ),
    ].slice(0, NAIPS_MAX_AREAS),
  };
}

function bounds(v: unknown): GeoBounds | null {
  if (!isObject(v)) return null;
  const n = (k: string): number | null =>
    typeof v[k] === 'number' && Number.isFinite(v[k]) ? (v[k] as number) : null;
  const s = n('south');
  const w = n('west');
  const no = n('north');
  const e = n('east');
  return s === null || w === null || no === null || e === null
    ? null
    : { south: s, west: w, north: no, east: e };
}

/**
 * Stored image overlays made safe: each needs an id, a path and bounds; opacity clamped; one per id; an alignment
 * by control points kept when readable (refitted from its points, decision 375).
 */
function images(v: unknown): MapImageOverlay[] {
  if (!Array.isArray(v)) return [];
  const seen = new Set<string>();
  const out: MapImageOverlay[] = [];
  for (const x of v) {
    if (!isObject(x) || typeof x.id !== 'string' || typeof x.path !== 'string' || seen.has(x.id)) continue;
    const b = bounds(x.bounds);
    if (!b) continue;
    seen.add(x.id);
    const alignment = normaliseImageAlignment(x.alignment);
    out.push({
      id: x.id,
      path: x.path,
      name: typeof x.name === 'string' ? x.name : (x.path.split(/[\\/]/).pop() ?? x.path),
      bounds: (alignment && alignedBounds(alignment)) ?? b,
      opacity: clampOpacity(x.opacity) ?? 0.8,
      ...(alignment ? { alignment } : {}),
    });
  }
  return out;
}

/**
 * A stored map state made safe: unknown layers, base maps and overlays dropped, opacities clamped to 0.1–1,
 * duplicate pins removed. Anything unreadable falls back to the defaults (the map always opens).
 */
export function normaliseMapState(raw: unknown, first: FirstLaunchLayers | null = null): MapState {
  if (!isObject(raw)) return { ...initialMapState(), prefs: firstLaunchPrefs(first) };
  // a map nobody has changed yet starts with the organisation's first-launch layers (05-005, decision 411)
  if (!isObject(raw.prefs) && first) {
    const rest = normaliseMapState({ ...raw, prefs: firstLaunchPrefs(first) });
    return rest;
  }
  const version =
    typeof raw.version === 'number' && Number.isInteger(raw.version) && raw.version >= 0 ? raw.version : 0;
  const p = isObject(raw.prefs) ? raw.prefs : {};
  const baseIds = new Set<string>(BASE_LAYERS.map((b) => b.id));
  const overlayIds = new Set<string>(OVERLAYS.map((o) => o.id));
  const layerIds = new Set<string>(MAP_LAYERS.map((l) => l.id));
  const layers = { ...DEFAULT_MAP_PREFS.layers } as Record<MapLayerId, boolean>;
  if (isObject(p.layers)) {
    for (const [k, v] of Object.entries(p.layers))
      if (layerIds.has(k) && typeof v === 'boolean') layers[k as MapLayerId] = v;
  }
  const opacity: Partial<Record<MapLayerId, number>> = {};
  if (isObject(p.opacity)) {
    for (const [k, v] of Object.entries(p.opacity)) {
      const c = clampOpacity(v);
      if (layerIds.has(k) && c !== null) opacity[k as MapLayerId] = c;
    }
  }
  const seen = new Set<string>();
  const pinned: PinnedItem[] = [];
  if (Array.isArray(p.pinned)) {
    for (const item of p.pinned) {
      if (!isObject(item) || typeof item.key !== 'string' || seen.has(item.key)) continue;
      const b = bounds(item.bounds);
      if (!b) continue;
      seen.add(item.key);
      pinned.push({
        key: item.key,
        label: typeof item.label === 'string' ? item.label : item.key,
        bounds: b,
      });
    }
  }
  const l = isObject(raw.layers) ? raw.layers : {};
  const parcels = Array.isArray(l.landholders)
    ? (l.landholders.filter(
        (x) => isObject(x) && typeof x.id === 'string' && isObject(x.geometry),
      ) as unknown as LandParcel[])
    : [];
  return {
    version,
    prefs: {
      base:
        typeof p.base === 'string' && baseIds.has(p.base) ? (p.base as BaseLayerId) : DEFAULT_MAP_PREFS.base,
      overlays: [...new Set(strings(p.overlays).filter((o) => overlayIds.has(o)))] as OverlayId[],
      layers,
      opacity,
      hidden_hazard_folders: [...new Set(strings(p.hidden_hazard_folders))],
      coverage: p.coverage === true,
      pinned,
      weather: normaliseWeatherPrefs(p.weather),
      fleet_groups: groupList(strings(p.fleet_groups)),
      trail_hours: trailHours(p.trail_hours) ?? DEFAULT_MAP_PREFS.trail_hours,
      notam: normaliseNotamPrefs(p.notam),
      traffic: normalisePublicTraffic(p.traffic),
      trail_style: normaliseTrailStyle(p.trail_style),
      hidden_airspace: Array.isArray(p.hidden_airspace)
        ? ([
            ...new Set(strings(p.hidden_airspace).filter((g) => AIRSPACE_GROUP_IDS.has(g))),
          ] as AirspaceGroup[])
        : DEFAULT_HIDDEN_AIRSPACE,
      hidden_kml: [...new Set(strings(p.hidden_kml))],
      kml_3d: [...new Set(strings(p.kml_3d))],
      camera_sync: typeof p.camera_sync === 'boolean' ? p.camera_sync : DEFAULT_MAP_PREFS.camera_sync,
      auto_pin: p.auto_pin !== false,
      hidden_trails: [...new Set(strings(p.hidden_trails))],
      property_layers: [
        ...new Set(strings(p.property_layers).filter((x) => PROPERTY_IDS.has(x))),
      ] as PropertyLayerId[],
      property_opacity: propertyOpacities(p.property_opacity),
      hidden_landholders: [...new Set(strings(p.hidden_landholders))],
      landholder_fill: unitOpacity(p.landholder_fill) ?? DEFAULT_MAP_PREFS.landholder_fill,
      landholder_folders: [
        ...new Set(
          strings(p.landholder_folders)
            .map((f) => f.trim())
            .filter((f) => f !== ''),
        ),
      ],
      fire_planned_burns: p.fire_planned_burns === true,
    },
    layers: {
      hazard_files: [...new Set(strings(l.hazard_files))],
      kml_files: [...new Set(strings(l.kml_files))],
      lns_files: [...new Set(strings(l.lns_files))],
      images: images(l.images),
      landholders: parcels,
      elevation: parseElevationChoice(l.elevation),
    },
  };
}

/** The kinds of file a map shows by path. */
export type MapFileKind = 'hazard' | 'kml' | 'lns';

const FILE_LIST: Readonly<Record<MapFileKind, 'hazard_files' | 'kml_files' | 'lns_files'>> = {
  hazard: 'hazard_files',
  kml: 'kml_files',
  lns: 'lns_files',
};

export type MapAction =
  | { readonly type: 'set_layer'; readonly layer: MapLayerId; readonly on: boolean }
  | { readonly type: 'set_opacity'; readonly layer: MapLayerId; readonly opacity: number }
  | { readonly type: 'set_base'; readonly base: BaseLayerId }
  | { readonly type: 'set_overlay'; readonly overlay: OverlayId; readonly on: boolean }
  | { readonly type: 'set_hazard_folder'; readonly path: string; readonly visible: boolean }
  | { readonly type: 'set_coverage'; readonly on: boolean }
  | { readonly type: 'set_fire_planned_burns'; readonly on: boolean }
  | { readonly type: 'set_fleet_groups'; readonly groups: readonly string[] }
  | { readonly type: 'set_trail_hours'; readonly hours: number }
  | { readonly type: 'set_notam'; readonly patch: Partial<NotamPrefs> }
  | { readonly type: 'set_traffic_source'; readonly source: PublicTrafficId; readonly on: boolean }
  | { readonly type: 'set_traffic_radius'; readonly source: PublicTrafficId; readonly nm: number }
  | { readonly type: 'set_trail_style'; readonly patch: Partial<TrailStyle> }
  | { readonly type: 'set_airspace_group'; readonly group: AirspaceGroup; readonly on: boolean }
  | { readonly type: 'set_kml_visible'; readonly path: string; readonly visible: boolean }
  | { readonly type: 'view_kml_3d'; readonly path: string }
  | { readonly type: 'set_camera_sync'; readonly on: boolean }
  /** Back to the organisation's first-launch layers (overlays, layers, panels); the rest is kept. */
  | { readonly type: 'reset_layers'; readonly first: FirstLaunchLayers | null }
  | { readonly type: 'set_auto_pin'; readonly on: boolean }
  /** A state property layer on or off, and its opacity (04-021). */
  | { readonly type: 'set_property_layer'; readonly id: PropertyLayerId; readonly on: boolean }
  | { readonly type: 'set_property_opacity'; readonly id: PropertyLayerId; readonly opacity: number }
  /** Landholder boundaries shown or hidden on this laptop, the fill opacity, and folders not used yet (05-017). */
  | { readonly type: 'set_landholders_visible'; readonly ids: readonly string[]; readonly visible: boolean }
  | { readonly type: 'set_landholder_fill'; readonly opacity: number }
  | { readonly type: 'add_landholder_folder'; readonly name: string }
  | { readonly type: 'rename_landholder_folder'; readonly from: string; readonly to: string }
  | { readonly type: 'remove_landholder_folder'; readonly name: string }
  /** The landholder boundaries of the project's map file, moved to the shared record (decision 618). */
  | { readonly type: 'clear_legacy_landholders' }
  | { readonly type: 'set_trail_hidden'; readonly callsign: string; readonly hidden: boolean }
  | { readonly type: 'pin'; readonly item: PinnedItem }
  | { readonly type: 'unpin'; readonly key: string }
  | { readonly type: 'add_file'; readonly kind: MapFileKind; readonly path: string }
  | { readonly type: 'remove_file'; readonly kind: MapFileKind; readonly path: string }
  | { readonly type: 'add_image'; readonly image: MapImageOverlay }
  | { readonly type: 'remove_image'; readonly id: string }
  /** Place an image by its control points (decision 375), or back to its box with null. */
  | { readonly type: 'set_image_alignment'; readonly id: string; readonly alignment: ImageAlignment | null }
  | { readonly type: 'set_image_opacity'; readonly id: string; readonly opacity: number }
  | { readonly type: 'add_landholders'; readonly parcels: readonly LandParcel[] }
  | {
      readonly type: 'update_landholder';
      readonly id: string;
      readonly patch: Partial<
        Pick<LandParcel, 'name' | 'notes' | 'needs_notification' | 'objection' | 'visible' | 'folder'>
      >;
    }
  | { readonly type: 'remove_landholder'; readonly id: string }
  /** The project's elevation for the 3D view (Project settings). */
  | { readonly type: 'set_elevation'; readonly choice: ElevationChoice }
  | { readonly type: 'weather_mode'; readonly mode: WeatherModeId }
  | { readonly type: 'set_weather_widget'; readonly widget: WeatherWidgetId; readonly on: boolean }
  | { readonly type: 'set_weather_opacity'; readonly layer: WeatherCanvasId; readonly opacity: number }
  | { readonly type: 'set_radar_coverage'; readonly on: boolean }
  | { readonly type: 'set_radar_coverage_opacity'; readonly opacity: number };

/** Which part of the state an action changes (the core saves only that file). */
export function actionPart(a: MapAction): 'prefs' | 'layers' {
  return a.type === 'add_file' ||
    a.type === 'remove_file' ||
    a.type === 'add_image' ||
    a.type === 'remove_image' ||
    a.type === 'set_image_alignment' ||
    a.type === 'set_image_opacity' ||
    a.type === 'add_landholders' ||
    a.type === 'update_landholder' ||
    a.type === 'remove_landholder' ||
    a.type === 'set_elevation' ||
    a.type === 'clear_legacy_landholders'
    ? 'layers'
    : 'prefs';
}

const toggle = <T>(list: readonly T[], item: T, on: boolean): T[] =>
  on ? (list.includes(item) ? [...list] : [...list, item]) : list.filter((x) => x !== item);

/** The state after `action` (the version is the core's to change). */
export function applyMapAction(state: MapState, action: MapAction): MapState {
  const prefs = state.prefs;
  const layers = state.layers;
  const withPrefs = (p: Partial<MapPrefs>): MapState => ({ ...state, prefs: { ...prefs, ...p } });
  const withLayers = (l: Partial<MapProjectLayers>): MapState => ({ ...state, layers: { ...layers, ...l } });
  switch (action.type) {
    case 'set_layer':
      return withPrefs({ layers: { ...prefs.layers, [action.layer]: action.on } });
    case 'set_opacity':
      return withPrefs({ opacity: { ...prefs.opacity, [action.layer]: clampOpacity(action.opacity) ?? 1 } });
    case 'set_base':
      return withPrefs({ base: action.base });
    case 'set_overlay':
      return withPrefs({ overlays: toggle(prefs.overlays, action.overlay, action.on) });
    case 'set_hazard_folder':
      // hidden: the path; shown: `show:<path>`, which also shows a folder its file hides (bug #56)
      return withPrefs({
        hidden_hazard_folders: toggle(
          toggle(prefs.hidden_hazard_folders, action.path, !action.visible),
          `show:${action.path}`,
          action.visible,
        ),
      });
    case 'set_coverage':
      return withPrefs({ coverage: action.on });
    case 'set_fire_planned_burns':
      return withPrefs({ fire_planned_burns: action.on });
    case 'set_fleet_groups':
      return withPrefs({ fleet_groups: groupList(action.groups) });
    case 'set_trail_hours':
      return withPrefs({
        trail_hours:
          trailHours(action.hours) ??
          HISTORY_DEPTH_HOURS.find((h) => h >= action.hours) ??
          HISTORY_DEPTH_HOURS[HISTORY_DEPTH_HOURS.length - 1]!,
      });
    case 'set_notam':
      return withPrefs({ notam: normaliseNotamPrefs({ ...prefs.notam, ...action.patch }) });
    case 'set_traffic_source':
      return withPrefs({
        traffic: normalisePublicTraffic({
          ...prefs.traffic,
          sources: toggle(prefs.traffic.sources, action.source, action.on),
        }),
      });
    case 'set_traffic_radius':
      return withPrefs({
        traffic: normalisePublicTraffic({
          ...prefs.traffic,
          radius_nm: { ...prefs.traffic.radius_nm, [action.source]: action.nm },
        }),
      });
    case 'set_trail_style':
      return withPrefs({ trail_style: normaliseTrailStyle({ ...prefs.trail_style, ...action.patch }) });
    case 'set_airspace_group':
      return withPrefs({ hidden_airspace: toggle(prefs.hidden_airspace, action.group, !action.on) });
    case 'set_kml_visible':
      return withPrefs({ hidden_kml: toggle(prefs.hidden_kml, action.path, !action.visible) });
    case 'view_kml_3d':
      return withPrefs({ kml_3d: [...prefs.kml_3d.filter((p) => p !== action.path), action.path] });
    case 'set_camera_sync':
      return withPrefs({ camera_sync: action.on });
    case 'reset_layers': {
      const f = firstLaunchPrefs(action.first);
      return withPrefs({ overlays: f.overlays, layers: f.layers, weather: f.weather });
    }
    case 'set_auto_pin':
      return withPrefs({ auto_pin: action.on });
    case 'set_property_layer':
      // kept in the tool's order, so the identify order never depends on the order switched on
      return withPrefs({
        property_layers: PROPERTY_LAYER_IDS.filter((id) =>
          id === action.id ? action.on : prefs.property_layers.includes(id),
        ),
      });
    case 'set_landholders_visible': {
      const ids = new Set(action.ids);
      const rest = prefs.hidden_landholders.filter((id) => !ids.has(id));
      return withPrefs({ hidden_landholders: action.visible ? rest : [...rest, ...action.ids] });
    }
    case 'set_landholder_fill': {
      const o = unitOpacity(action.opacity);
      return o === null ? state : withPrefs({ landholder_fill: o });
    }
    case 'add_landholder_folder': {
      const n = action.name.trim();
      return !n || prefs.landholder_folders.includes(n)
        ? state
        : withPrefs({ landholder_folders: [...prefs.landholder_folders, n] });
    }
    case 'rename_landholder_folder': {
      const to = action.to.trim();
      return withPrefs({
        landholder_folders: [
          ...new Set(prefs.landholder_folders.map((f) => (f === action.from ? to : f)).filter(Boolean)),
        ],
      });
    }
    case 'remove_landholder_folder':
      return withPrefs({ landholder_folders: prefs.landholder_folders.filter((f) => f !== action.name) });
    case 'clear_legacy_landholders':
      return withLayers({ landholders: [] });
    case 'set_property_opacity': {
      const o = unitOpacity(action.opacity);
      return o === null
        ? state
        : withPrefs({ property_opacity: { ...prefs.property_opacity, [action.id]: o } });
    }
    case 'set_trail_hidden': {
      const rest = prefs.hidden_trails.filter((c) => c !== action.callsign);
      return withPrefs({ hidden_trails: action.hidden ? [...rest, action.callsign] : rest });
    }
    case 'pin':
      return withPrefs({ pinned: [...prefs.pinned.filter((p) => p.key !== action.item.key), action.item] });
    case 'unpin':
      return withPrefs({ pinned: prefs.pinned.filter((p) => p.key !== action.key) });
    case 'add_file':
    case 'remove_file': {
      const list = FILE_LIST[action.kind];
      return withLayers({ [list]: toggle(layers[list], action.path, action.type === 'add_file') });
    }
    case 'add_image':
      return withLayers({ images: [...layers.images.filter((i) => i.id !== action.image.id), action.image] });
    case 'remove_image':
      return withLayers({ images: layers.images.filter((i) => i.id !== action.id) });
    case 'set_image_alignment':
      return withLayers({
        images: layers.images.map((i) => {
          if (i.id !== action.id) return i;
          // fitted again here, so the transform kept always agrees with the points
          const alignment = action.alignment ? normaliseImageAlignment(action.alignment) : null;
          const rest: MapImageOverlay = {
            id: i.id,
            path: i.path,
            name: i.name,
            bounds: i.bounds,
            opacity: i.opacity,
          };
          if (!alignment) return rest;
          return { ...rest, bounds: alignedBounds(alignment) ?? i.bounds, alignment };
        }),
      });
    case 'set_image_opacity':
      return withLayers({
        images: layers.images.map((i) =>
          i.id === action.id ? { ...i, opacity: clampOpacity(action.opacity) ?? i.opacity } : i,
        ),
      });
    case 'add_landholders':
      return withLayers({ landholders: [...layers.landholders, ...action.parcels] });
    case 'update_landholder':
      return withLayers({
        landholders: layers.landholders.map((p) => (p.id === action.id ? { ...p, ...action.patch } : p)),
      });
    case 'remove_landholder':
      return withLayers({ landholders: layers.landholders.filter((p) => p.id !== action.id) });
    case 'set_elevation':
      return withLayers({ elevation: parseElevationChoice(action.choice) });
    case 'weather_mode': {
      const r = toggleWeatherMode(prefs.weather, prefs.overlays, action.mode);
      return withPrefs({ weather: r.weather, overlays: r.overlays });
    }
    case 'set_weather_widget':
      return withPrefs({ weather: setWeatherWidget(prefs.weather, action.widget, action.on) });
    case 'set_weather_opacity':
      return withPrefs({
        weather: {
          ...prefs.weather,
          opacity: { ...prefs.weather.opacity, [action.layer]: clampOpacity(action.opacity) ?? 1 },
        },
      });
    case 'set_radar_coverage':
      return withPrefs({ weather: { ...prefs.weather, radar_coverage: action.on } });
    case 'set_radar_coverage_opacity':
      return withPrefs({
        weather: {
          ...prefs.weather,
          radar_coverage_opacity: Number.isFinite(action.opacity)
            ? Math.min(RADAR_COVERAGE_MAX, Math.max(RADAR_COVERAGE_MIN, action.opacity))
            : prefs.weather.radar_coverage_opacity,
        },
      });
  }
}

/** The opacity of a layer (1 unless set). */
export const layerOpacity = (prefs: MapPrefs, layer: MapLayerId): number => prefs.opacity[layer] ?? 1;

// Per-window view state (not shared)

/** What one map window shows itself: never written to the shared state. */
export interface MapWindowView {
  readonly center: { readonly lat: number; readonly lng: number } | null;
  readonly zoom: number | null;
  /** The aircraft this window follows (Q69: each window can follow one aircraft), or null. */
  readonly follow: string | null;
}

export const INITIAL_WINDOW_VIEW: MapWindowView = { center: null, zoom: null, follow: null };

/** The default view with nothing to fit: Australia (the Diurnal map's [-25, 134] zoom 5, inventory 05 F1). */
export const DEFAULT_CENTRE = { lat: -25, lng: 134 } as const;
export const DEFAULT_ZOOM = 5;
